import React from "react";

/** The Finzora mark on its native 24x24 grid, shared with the files in `brand/`. */
const MARK_PATHS = [
  "m15.45 16.52-3.01-3.01c-.11-.11-.24-.13-.31-.13s-.2.02-.31.13L8.8 16.53c-.34.34-.87.89-2.64.89l3.71 3.7c1.17 1.17 3.07 1.17 4.24 0l3.72-3.71c-.91 0-1.67-.18-2.38-.89M8.8 7.47l3.02 3.02c.08.08.2.13.31.13s.23-.05.31-.13l2.99-2.99c.71-.74 1.52-.91 2.43-.91l-3.72-3.71c-1.17-1.17-3.07-1.17-4.24 0l-3.71 3.7c1.76 0 2.3.58 2.61.89",
  "m21.11 9.85-2.25-2.26H17.6c-.54 0-1.08.22-1.45.61l-3 3c-.28.28-.65.42-1.02.42-.36 0-.74-.15-1.02-.42L8.09 8.17c-.38-.38-.9-.6-1.45-.6H5.17l-2.29 2.3c-1.17 1.17-1.17 3.07 0 4.24l2.29 2.3h1.48c.54 0 1.06-.22 1.45-.6l3.02-3.02c.28-.28.65-.42 1.02-.42s.74.14 1.02.42l3.01 3.01c.38.38.9.6 1.45.6h1.26l2.25-2.26c1.17-1.18 1.17-3.1-.02-4.29",
];

/**
 * Finzora logo: the original mark on a deep indigo tile with a violet gradient.
 *
 * Two coordinate-space notes, both easy to get wrong:
 *  - `userSpaceOnUse` resolves against the user space where the gradient is
 *    *used*, and the mark group carries a scale(13.2) — so the gradient vector
 *    and the blur radius are expressed in the 24x24 grid, not canvas pixels.
 *  - The mark is a diamond, so its bbox corners are empty; the gradient runs
 *    along the height rather than corner to corner, or it would be wasted on
 *    blank space and render flat.
 */
const Logo: React.FC<{ size?: number; title?: string }> = ({
  size = 40,
  title = "Finzora",
}) => {
  // Fixed id rather than useId(): React 19 emits ids containing «», and
  // url(#...) references to those have historically broken in Safari.
  // Repeating identical <defs> is harmless.
  const id = "fz-logo";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      role="img"
      aria-label={title}
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <linearGradient
          id={`${id}-tile`}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="512"
          y2="512"
        >
          <stop offset="0" stopColor="#3B3187" />
          <stop offset="0.5" stopColor="#221F55" />
          <stop offset="1" stopColor="#0E1226" />
        </linearGradient>
        <radialGradient
          id={`${id}-glow`}
          gradientUnits="userSpaceOnUse"
          cx="128"
          cy="86"
          r="400"
        >
          <stop offset="0" stopColor="#8B84FF" stopOpacity="0.30" />
          <stop offset="1" stopColor="#8B84FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient
          id={`${id}-mark`}
          gradientUnits="userSpaceOnUse"
          x1="7"
          y1="3.4"
          x2="17"
          y2="20.6"
        >
          <stop offset="0" stopColor="#DCD7FF" />
          <stop offset="0.45" stopColor="#8F84FF" />
          <stop offset="1" stopColor="#4F42E6" />
        </linearGradient>
        <filter
          id={`${id}-lift`}
          x="-25%"
          y="-25%"
          width="150%"
          height="150%"
        >
          <feGaussianBlur stdDeviation="0.3" result="b" />
          <feColorMatrix
            in="b"
            type="matrix"
            values="0 0 0 0 0.55  0 0 0 0 0.52  0 0 0 0 1  0 0 0 0.38 0"
          />
        </filter>
      </defs>

      <rect width="512" height="512" rx="116" fill={`url(#${id}-tile)`} />
      <rect width="512" height="512" rx="116" fill={`url(#${id}-glow)`} />

      <g transform="translate(97.6 97.6) scale(13.2)">
        <g filter={`url(#${id}-lift)`} fill="#8B84FF">
          {MARK_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
        <g fill={`url(#${id}-mark)`}>
          {MARK_PATHS.map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
      </g>
    </svg>
  );
};

export default Logo;
